/**
 * Telefone na busca da lista e no "abrir conversa" dos inboxes de WhatsApp.
 *
 * A mesma pessoa aparece com e sem o 55 e com e sem o 9º dígito (5534991609492
 * e 553491609492), e o operador digita do jeito que lê: "34 99160-9492",
 * "+1 415 555 0123". Comparar texto cru não acha nada disso; comparar dígitos
 * exatos também não (auditoria de 25/09/2026: busca vazia e conversa duplicada
 * aberta pelo Kanban e pela "Nova conversa").
 *
 * Só vale para chave de TELEFONE. Chave opaca (instagram:, messenger:,
 * widget:), grupo e LID nunca passam por aqui: devolvem '' e não casam.
 */
import { brPhoneVariants } from './phone';
import { isOpaqueKey } from './channels';
import { isBrNationalNumber } from '../components/contacts/createPhoneRule';

// A partir de quantos dígitos o termo da busca vira busca de telefone.
export const PHONE_SEARCH_MIN_DIGITS = 8;

// Termo com cara de telefone: só dígitos e pontuação de telefone.
const PHONE_LIKE = /^[\d\s+().-]+$/;

const onlyDigits = (valor) => String(valor || '').replace(/\D/g, '');

/** Dígitos do termo quando ele é busca de telefone (8+ dígitos); senão ''. */
export const phoneSearchDigits = (term) => {
  const t = String(term || '').trim();
  if (!t || !PHONE_LIKE.test(t)) return '';
  const d = onlyDigits(t);
  return d.length >= PHONE_SEARCH_MIN_DIGITS ? d : '';
};

/** Dígitos do telefone de uma chave de conversa (número cru ou JID).
 *  '' para chave opaca, grupo, LID e qualquer coisa que não seja telefone. */
export const phoneKeyDigits = (key) => {
  const s = String(key || '').trim();
  if (!s || isOpaqueKey(s) || s.includes(':')) return '';
  if (/@(g\.us|lid|broadcast|newsletter)$/i.test(s)) return '';
  const base = s.split('@')[0];
  if (/[a-z]/i.test(base)) return '';
  return onlyDigits(base);
};

// Formas do termo: ele mesmo, com e sem o 9º dígito e, se veio com 55, sem o 55.
const termForms = (t) => {
  const out = new Set(brPhoneVariants(t));
  if (t.startsWith('55') && (t.length === 12 || t.length === 13)) {
    [...out].forEach((v) => out.add(v.slice(2)));
  }
  return [...out];
};

/**
 * O termo da lupa acha esta conversa pelo telefone?
 * Casa por trecho (o operador pode digitar só o final), tolerando 55 e 9º dígito.
 */
export const matchesPhoneSearch = (key, term) => {
  const t = phoneSearchDigits(term);
  if (!t) return false;
  const d = phoneKeyDigits(key);
  if (!d) return false;
  const chatForms = brPhoneVariants(d);
  const forms = termForms(t);
  return chatForms.some((c) => forms.some((f) => c.includes(f)));
};

// Formas completas de um telefone: DDD + número brasileiro ganha o 55; depois,
// com e sem o 9º dígito, só na faixa de celular (os 8 dígitos começam com 6 a
// 9). Aqui a variante decide QUAL conversa abrir, então um fixo não casa com
// um "9" fabricado na frente dele.
const fullForms = (raw) => {
  const d = phoneKeyDigits(raw);
  if (!d) return [];
  const full = (d.length === 10 || d.length === 11) && isBrNationalNumber(d) ? `55${d}` : d;
  const out = [full];
  if (full.startsWith('55')) {
    const ddd = full.slice(2, 4);
    const resto = full.slice(4);
    if (full.length === 13 && /^9[6-9]/.test(resto)) out.push(`55${ddd}${resto.slice(1)}`);
    else if (full.length === 12 && /^[6-9]/.test(resto)) out.push(`55${ddd}9${resto}`);
  }
  return out;
};

/** Dois telefones são a mesma pessoa? (igualdade, não trecho) */
export const samePhone = (a, b) => {
  const fa = fullForms(a);
  if (!fa.length) return false;
  const fb = new Set(fullForms(b));
  return fa.some((x) => fb.has(x));
};

/**
 * Conversa carregada que é deste telefone. O igual exato vence a variante: se
 * as duas threads existem (com e sem o 9), abre a que tem o número pedido.
 */
export const findChatByPhone = (chats, phone, keyOf) => {
  const lista = Array.isArray(chats) ? chats : [];
  const alvo = phoneKeyDigits(phone);
  if (!alvo) return null;
  return (
    lista.find((c) => phoneKeyDigits(keyOf(c)) === alvo)
    || lista.find((c) => samePhone(keyOf(c), alvo))
    || null
  );
};
